import { useQueryClient } from '@tanstack/react-query';

import { gql } from '@/utilities/gql';
import { makeGqlMutation } from '@/utilities/gqlQuery';
import { useSnackbar } from '@/features/snackbar';

import type { PortfolioQueryClientData } from '@/hooks/portfolio/usePortfolio';
import {
  PORTFOLIO_QUERY_KEY,
  PORTFOLIO_FRAGMENT,
  PORTFOLIOS_QUERY_KEY,
} from '@/hooks/portfolio/usePortfolio';
import { AGGREGATE_PORTFOLIO_QUERY_KEY } from '@/hooks/portfolio/useAggregatePortfolio';
import { PORTFOLIOS_PERFORMANCE_TIMESERIES_QUERY_KEY } from '@/hooks/portfolio/usePortfolioPerformanceTimeseries';

import type { ResultOf } from '@/utilities/gql';

const MUTATION = gql(
  `
  mutation updateHoldingTransactions($id: String!, $transactions: [ManualTransactionInput!]!) {
    updateHoldingTransactions(id: $id, transactions: $transactions) {
      ...portfolioFields
    }
  }
`,
  [PORTFOLIO_FRAGMENT]
);

const mutation = makeGqlMutation(MUTATION);

interface Props {
  onSuccess?: (data: ResultOf<typeof MUTATION>) => void;
}

export const useUpdateHoldingTransactions = ({ onSuccess }: Props = {}) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return mutation({
    onSuccess: (data) => {
      addSnackbar({
        type: 'positive',
        message: `All changes saved successfully!`,
        lifeTime: 3000,
      });
      const portfolio = data.updateHoldingTransactions;
      queryClient.setQueryData<PortfolioQueryClientData>(
        [PORTFOLIO_QUERY_KEY, portfolio.id],
        () => portfolio
      );
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIO_QUERY_KEY, portfolio.id],
      });
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: AGGREGATE_PORTFOLIO_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIOS_PERFORMANCE_TIMESERIES_QUERY_KEY, portfolio.id],
      });
      onSuccess?.(data);
    },
  });
};
